"use client"

import { useState } from "react"
import { calculateHerbicideTiming, type HerbicideTimingResult } from "@/lib/herbicide-timing-calculator"
import { Bug, ArrowRight, RotateCcw } from "lucide-react"
import Link from "next/link"
import { ZIP_INPUT_ATTRS, sanitizeZip } from "@/lib/zip-input"
import { analytics } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"

const GRASS_TYPES = [
  { value: "Kentucky Bluegrass", label: "Kentucky Bluegrass" },
  { value: "Bermudagrass", label: "Bermuda" },
  { value: "St. Augustinegrass", label: "St. Augustine" },
  { value: "Zoysiagrass", label: "Zoysia" },
  { value: "Tall Fescue", label: "Tall Fescue" },
  { value: "Fine Fescue", label: "Fine Fescue" },
  { value: "Perennial Ryegrass", label: "Ryegrass" },
  { value: "Centipede", label: "Centipede" },
  { value: "Bahiagrass", label: "Bahia" },
  { value: "Buffalograss", label: "Buffalo" },
]

const URGENCY_STYLES: Record<string, string> = {
  critical: "bg-red-50 border-red-200 text-red-700",
  optimal: "bg-green-50 border-green-200 text-green-700",
  late: "bg-amber-50 border-amber-200 text-amber-700",
  "too-late": "bg-gray-50 border-gray-200 text-gray-500",
}

interface MiniHerbicideTimingProps {
  /** Post the widget was injected into. Rides both events so the embedded-widget
   *  arm is separable from the link-CTA arm on the same cluster. */
  slug?: string
  /** Marker's data-variant, e.g. "blog-intro". */
  variant?: string
}

/**
 * Embedded pre-emergent timing widget.
 *
 * Instrumented deliberately: this component shipped with no analytics at all, so
 * the one thing that makes an embed worth doing over a link — comparing their
 * rates — was unreadable. The impression fires on the same `cta_surface_shown`
 * contract the inline link CTAs use, and a completed calculation fires
 * `calculator_used` from the intentional `form_submit` source, which is one of
 * the events the funnel audit counts as entering a funnel. Together they make a
 * blog post's embed comparable to `blog_inline_diagnose` and friends.
 */
export function MiniHerbicideTiming({ slug, variant }: MiniHerbicideTimingProps = {}) {
  const [zipCode, setZipCode] = useState("")
  const [grassType, setGrassType] = useState("")
  const [result, setResult] = useState<HerbicideTimingResult | null>(null)
  const [error, setError] = useState("")

  const ctaLocation = variant === "blog-intro" ? "blog_intro_herbicide_widget" : "blog_inline_herbicide_widget"
  const impressionRef = useImpressionEvent<HTMLDivElement>(
    ctaLocation,
    { surface: "mini_herbicide_timing", article_slug: slug, slot: variant },
    { emit: analytics.ctaSurfaceShown },
  )

  const handleCalculate = () => {
    setError("")
    if (!/^\d{5}$/.test(zipCode)) {
      setError("Enter a valid 5-digit ZIP code")
      return
    }
    if (!grassType) {
      setError("Select your grass type")
      return
    }

    try {
      const calcResult = calculateHerbicideTiming(
        zipCode,
        grassType,
        ["Crabgrass"],
        "pre-emergent"
      )

      // Calculator returns { error: '...' } instead of throwing
      if ((calcResult as any).error) {
        setError((calcResult as any).error)
        return
      }

      if (!calcResult.preEmergentTiming) {
        setError("No pre-emergent timing data available for this location right now.")
        return
      }

      setResult(calcResult)
      analytics.calculatorUsed('herbicide', {
        inputs: { zip_code: zipCode, grass_type: grassType },
        location: zipCode,
        source: 'form_submit',
      })
    } catch {
      setError("Could not calculate timing for this ZIP code.")
    }
  }

  const handleReset = () => {
    setZipCode("")
    setGrassType("")
    setResult(null)
    setError("")
  }

  const urgency = result?.preEmergentTiming?.urgencyLevel || "optimal"
  const urgencyStyle = URGENCY_STYLES[urgency] || URGENCY_STYLES.optimal

  return (
    <div
      ref={impressionRef}
      className="my-8 mx-auto max-w-md rounded-xl border-2 border-green-200 bg-linear-to-b from-green-50/80 to-white overflow-hidden shadow-xs inline-widget"
    >
      <div className="flex items-center gap-2 px-5 py-3 bg-green-600">
        <Bug className="w-4 h-4 text-white" />
        <h3 className="text-sm font-bold text-white">When Should You Apply Pre-Emergent?</h3>
      </div>

      {!result ? (
        <div className="p-5 space-y-3">
          <div>
            <label htmlFor="mini-herb-zip" className="block text-sm font-medium text-gray-700 mb-1">
              ZIP Code
            </label>
            <input
              id="mini-herb-zip"
              {...ZIP_INPUT_ATTRS}
              placeholder="e.g., 30301"
              value={zipCode}
              onChange={(e) => setZipCode(sanitizeZip(e.target.value))}
              className="w-full h-10 px-3 rounded-lg border border-gray-300 text-base focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label htmlFor="mini-herb-grass" className="block text-sm font-medium text-gray-700 mb-1">
              Grass Type
            </label>
            <select
              id="mini-herb-grass"
              value={grassType}
              onChange={(e) => setGrassType(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-gray-300 text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
            >
              <option value="">Select grass type</option>
              {GRASS_TYPES.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>

          {error && <p className="text-red-600 text-xs">{error}</p>}

          <button
            onClick={handleCalculate}
            className="w-full h-10 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            Check My Timing
          </button>
        </div>
      ) : (
        <div className="p-5 space-y-3">
          <div className="text-center text-xs text-gray-500 mb-1">
            {result.region} Region
          </div>

          {result.preEmergentTiming && (
            <div className={`text-center p-3 rounded-lg border ${urgencyStyle}`}>
              <p className="text-xs font-medium mb-1 opacity-80">
                Apply {result.preEmergentTiming.soilTempRequirement} soil temp
              </p>
              <p className="text-base font-bold">{result.preEmergentTiming.optimalWindow}</p>
              <p className="text-xs mt-1 opacity-70">{result.preEmergentTiming.status}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleReset}
              className="h-9 flex items-center justify-center gap-1 border border-gray-300 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RotateCcw className="w-3 h-3" /> Retry
            </button>
            <Link
              href="/tools/herbicide-timing"
              className="h-9 flex items-center justify-center gap-1 bg-green-600 text-white text-xs font-medium no-underline rounded-lg hover:bg-green-700 transition-colors"
            >
              Full Calculator <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
